import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "message", "spinner", "preview", "successIcon", "errorIcon", "checkingIcon"]
  static values = {
    checkUrl: String,
    debounceMs: { type: Number, default: 1000 },
    checking: { type: String, default: "Checking availability..." },
    tooShort: { type: String, default: "Subdomain must be at least 3 characters long" },
    tooLong: { type: String, default: "Subdomain must be less than 64 characters long" },
    invalidChars: { type: String, default: "Subdomain can only contain lowercase letters, numbers, and hyphens" },
    consecutiveHyphens: { type: String, default: "Subdomain cannot contain consecutive hyphens" },
    checkError: { type: String, default: "Error checking subdomain availability. Please try again." },
    previewLabel: { type: String, default: "Your client booking site will be:" }
  }

  connect() {
    this.checkUrlValue = this.checkUrlValue || "/settings/check_subdomain"
    this.timeout = null
    this.isChecking = false
    this.minSpinnerTime = 500 // Minimum time to show spinner (500ms)
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  validateSubdomain() {
    // Clear any existing timeout
    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    // Clear previous state
    this.hideSpinner()
    this.clearMessage()

    const subdomain = this.getSubdomainValue()

    // If empty, just clear everything
    if (!subdomain) {
      this.hidePreview()
      return
    }

    // Show immediate client-side validation
    const validationError = this.getClientSideValidationError(subdomain)
    if (validationError) {
      this.showMessage(validationError, "error")
      this.hidePreview()
      return
    }

    // Show checking state and debounce the API call
    this.showSpinner()
    this.showMessage(this.checkingValue, "checking")

    this.timeout = setTimeout(() => {
      this.checkSubdomainAvailability()
    }, this.debounceValue)
  }

  checkSubdomainAvailability() {
    if (this.isChecking) return

    const subdomain = this.getSubdomainValue()
    if (!subdomain) {
      this.hideSpinner()
      this.clearMessage()
      return
    }

    this.isChecking = true
    const startTime = Date.now()

    const url = new URL(this.checkUrlValue, window.location.origin)
    url.searchParams.set('subdomain_to_check', subdomain)

    fetch(url.toString(), {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        return response.json()
      })
      .then(data => {
        // Calculate how long the request took
        const elapsed = Date.now() - startTime
        const remainingTime = Math.max(0, this.minSpinnerTime - elapsed)

        // Wait for the minimum spinner time before hiding it
        setTimeout(() => {
          this.hideSpinner()

          if (data.available) {
            this.showMessage(data.message, "success")
            this.showPreview(subdomain)
          } else {
            this.showMessage(data.message, "error")
            this.hidePreview()
          }
        }, remainingTime)
      })
      .catch(error => {
        console.error('Error checking subdomain:', error)

        // Also apply minimum time for error states
        const elapsed = Date.now() - startTime
        const remainingTime = Math.max(0, this.minSpinnerTime - elapsed)

        setTimeout(() => {
          this.hideSpinner()
          this.showMessage(this.checkErrorValue, "error")
        }, remainingTime)
      })
      .finally(() => {
        this.isChecking = false
      })
  }

  getSubdomainValue() {
    const input = this.hasInputTarget ? this.inputTarget : this.element.querySelector('input[name="account[subdomain]"]')
    return input ? input.value.trim() : ""
  }

  getClientSideValidationError(subdomain) {
    if (subdomain.length < 3) {
      return this.tooShortValue
    }

    if (subdomain.length > 63) {
      return this.tooLongValue
    }

    if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(subdomain)) {
      return this.invalidCharsValue
    }

    if (subdomain.includes("--")) {
      return this.consecutiveHyphensValue
    }

    return null
  }

  showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden")
    }
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden")
    }
  }

  iconHtmlFor(type) {
    switch (type) {
      case "success":
        return this.hasSuccessIconTarget ? this.successIconTarget.innerHTML : ""
      case "error":
        return this.hasErrorIconTarget ? this.errorIconTarget.innerHTML : ""
      case "checking":
        return this.hasCheckingIconTarget ? this.checkingIconTarget.innerHTML : ""
      default:
        return ""
    }
  }

  showMessage(message, type) {
    const messageElement = this.hasMessageTarget ? this.messageTarget : document.getElementById('subdomain-message')
    if (!messageElement) return

    messageElement.innerHTML = this.iconHtmlFor(type) + message
    messageElement.classList.remove("text-green-600", "text-red-600", "text-gray-500", "text-blue-600")

    switch (type) {
      case "success":
        messageElement.classList.add("text-green-600")
        break
      case "error":
        messageElement.classList.add("text-red-600")
        break
      case "checking":
        messageElement.classList.add("text-blue-600")
        break
      default:
        messageElement.classList.add("text-gray-500")
    }
  }

  clearMessage() {
    const messageElement = this.hasMessageTarget ? this.messageTarget : document.getElementById('subdomain-message')
    if (messageElement) {
      messageElement.innerHTML = ""
      messageElement.classList.remove("text-green-600", "text-red-600", "text-gray-500", "text-blue-600")
    }
  }

  showPreview(subdomain) {
    if (this.hasPreviewTarget) {
      const domain = window.location.hostname === 'localhost' ? 'fitnito.local' : 'fitnito.com'
      this.previewTarget.innerHTML = `
        <div class="text-sm text-gray-600 mt-2">
          <span class="text-gray-500">${this.previewLabelValue}</span>
          <div class="font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded-sm mt-1 inline-block">
            https://${subdomain}.${domain}
          </div>
        </div>
      `
      this.previewTarget.classList.remove("hidden")
    }
  }

  hidePreview() {
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("hidden")
    }
  }
}
